import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, Upload, Download, Plus } from 'lucide-react';

const LIST_TYPES = [
  { key: 'names', label: 'Names (Customers/Vendors/Employees)' },
  { key: 'items', label: 'Items' },
  { key: 'classes', label: 'Classes' },
  { key: 'terms', label: 'Terms' },
  { key: 'payment-methods', label: 'Payment Methods' },
  { key: 'customer-types', label: 'Customer Types' },
  { key: 'vendor-types', label: 'Vendor Types' },
  { key: 'job-types', label: 'Job Types' },
  { key: 'customer-messages', label: 'Customer Messages' },
];

export default function Lists({ base, isDark = true }) {
  const [listType, setListType] = useState('names');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [nameType, setNameType] = useState('customer');
  const [dry, setDry] = useState(null);
  const [importTaxIds, setImportTaxIds] = useState(false);
  const fileRef = useRef(null);
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows((await axios.get(`${base}/lists/${listType}`)).data || []); }
    catch (e) { setError(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [base, listType]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newName.trim()) return;
    const body = { name: newName.trim() };
    if (listType === 'names') body.name_type = nameType;
    try { await axios.post(`${base}/lists/${listType}`, body); setNewName(''); await load(); }
    catch (e) { setError(e?.response?.data?.detail || 'Add failed'); }
  };

  const runDry = async () => {
    const f = fileRef.current?.files?.[0]; if (!f) { setError('Choose an .IIF file'); return; }
    const fd = new FormData(); fd.append('file', f); fd.append('import_tax_ids', String(importTaxIds));
    try { setDry((await axios.post(`${base}/import/iif/dry-run`, fd)).data); setError(''); }
    catch (e) { setError(e?.response?.data?.detail || 'Dry-run failed'); }
  };
  const commit = async () => {
    const f = fileRef.current?.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('file', f); fd.append('import_tax_ids', String(importTaxIds));
    try { await axios.post(`${base}/import/iif/commit`, fd); setDry(null); if (fileRef.current) fileRef.current.value=''; await load(); }
    catch (e) { setError(e?.response?.data?.detail || 'Commit failed'); }
  };

  const rt = (r) => r.name_type || r.type || '';

  return (
    <div className="space-y-3" data-testid="olfin-lists">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Select value={listType} onValueChange={setListType}>
          <SelectTrigger className="w-80" data-testid="lists-type-picker"><SelectValue /></SelectTrigger>
          <SelectContent>{LIST_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2">
          <a href={`${base}/export/iif`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="lists-export-iif"><Download className="h-4 w-4 mr-1" />Export IIF</Button></a>
          <a href={`${base}/export/csv/${listType}`} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="lists-export-csv"><Download className="h-4 w-4 mr-1" />Export CSV</Button></a>
        </div>
      </div>
      {error && <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-400" data-testid="lists-error">{error}</div>}

      {/* IIF import */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <input type="file" accept=".iif,.IIF" ref={fileRef} className={`text-sm ${sub}`} data-testid="lists-import-file" />
            <label className={`text-xs flex items-center gap-1 ${sub}`}><input type="checkbox" checked={importTaxIds} onChange={(e) => setImportTaxIds(e.target.checked)} data-testid="lists-import-taxids" /> import tax IDs</label>
            <Button variant="outline" onClick={runDry} data-testid="lists-dryrun-btn"><Upload className="h-4 w-4 mr-1" />Dry-run</Button>
            {dry && <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={commit} data-testid="lists-commit-btn">Commit import</Button>}
          </div>
          {dry && (
            <div className="text-xs" data-testid="lists-dry-summary">
              <table className="w-full"><thead><tr className={sub}><th className="text-left p-1">Type</th><th>Create</th><th>Update</th><th>Skip</th><th>Error</th></tr></thead>
                <tbody>{Object.entries(dry).filter(([k, v]) => v && typeof v === 'object' && 'create' in v).map(([k, v]) => (
                  <tr key={k}><td className="p-1">{k}</td><td className="text-center">{v.create}</td><td className="text-center">{v.update}</td><td className="text-center">{v.skip}</td><td className="text-center">{v.error}</td></tr>
                ))}</tbody></table>
              {(dry.warnings || []).length > 0 && <p className="text-amber-500 mt-1">{dry.warnings.length} warning(s)</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* add + list */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New name…" className="max-w-xs" data-testid="lists-new-name" />
            {listType === 'names' && (
              <Select value={nameType} onValueChange={setNameType}>
                <SelectTrigger className="w-40" data-testid="lists-new-nametype"><SelectValue /></SelectTrigger>
                <SelectContent>{['customer', 'vendor', 'employee', 'other'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lists-add-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
          </div>
          {loading ? <div className={`flex items-center gap-2 ${sub}`}><Loader2 className="h-4 w-4 animate-spin" />Loading…</div> : (
            <table className="w-full text-sm"><thead><tr className={`text-left ${sub}`}><th className="p-1">Name</th><th className="p-1">Type</th><th className="p-1">Active</th></tr></thead>
              <tbody data-testid="lists-rows">
                {rows.map((r, i) => <tr key={i} className={r.is_active === false ? 'opacity-50' : ''}><td className="p-1">{r.full_name || r.name}</td><td className={`p-1 ${sub}`}>{rt(r)}</td><td className="p-1">{r.is_active === false ? 'no' : 'yes'}</td></tr>)}
                {rows.length === 0 && <tr><td colSpan={3} className={`p-2 ${sub}`} data-testid="lists-empty">No records.</td></tr>}
              </tbody></table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
